import { useState, type KeyboardEvent } from 'react';
import { Shield, ShieldAlert, KeyRound, FileText, Trash2, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Badge } from 'components/ui/badge';
import type {
  GuardrailCatalogParam,
  GuardrailConfig,
} from 'features/chat/agent-chat-module/types/agent.service.type';

type AgentConfigGuardrailCardProps = {
  guardrailId: string;
  name?: string;
  description?: string;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onRemove: () => void;
  disabled?: boolean;
  params?: Record<string, GuardrailCatalogParam>;
  config?: GuardrailConfig;
  onConfigChange?: (updatedConfig: GuardrailConfig) => void;
  errorMessage?: string;
};

const getGuardrailMeta = (id: string, t: (key: string, options?: any) => string) => {
  switch (id) {
    case 'secret':
      return {
        title: t('GUARDRAIL_SECRET_TITLE', { defaultValue: 'Secret Detection' }),
        description: t('GUARDRAIL_SECRET_DESC', {
          defaultValue:
            'Detects and protects against exposure of API keys, private credentials, and secrets.',
        }),
        icon: KeyRound,
      };
    case 'keyword':
      return {
        title: t('GUARDRAIL_KEYWORD_TITLE', { defaultValue: 'Keyword Filter' }),
        description: t('GUARDRAIL_KEYWORD_DESC', {
          defaultValue:
            'Restricts answers containing specific standalone keywords and terms (e.g. C++, class).',
        }),
        icon: FileText,
      };
    default:
      return {
        title: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' '),
        description: t('GUARDRAIL_GENERIC_DESC', {
          defaultValue: 'Enforces safety boundaries for agent interactions.',
        }),
        icon: ShieldAlert,
      };
  }
};

export function AgentConfigGuardrailCard({
  guardrailId,
  name,
  description,
  enabled,
  onToggleEnabled,
  onRemove,
  disabled = false,
  params,
  config,
  onConfigChange,
  errorMessage,
}: AgentConfigGuardrailCardProps) {
  const { t } = useTranslation();
  const meta = getGuardrailMeta(guardrailId, t);
  const Icon = meta.icon || Shield;

  const [keywordInput, setKeywordInput] = useState('');
  const [keywordError, setKeywordError] = useState<string | null>(null);

  const keywordsParam = params?.keywords;
  const maxItemLength = keywordsParam?.max_item_length ?? 64;
  const keywords: string[] = Array.isArray(config?.keywords) ? config.keywords : [];

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;

    if (trimmed.length > maxItemLength) {
      setKeywordError(
        t('GUARDRAIL_KEYWORD_MAX_LENGTH', {
          defaultValue: `Keyword cannot exceed ${maxItemLength} characters`,
          max: maxItemLength,
        })
      );
      return;
    }

    // Case-insensitive check
    const exists = keywords.some((kw) => kw.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setKeywordError(
        t('GUARDRAIL_KEYWORD_ALREADY_EXISTS', { defaultValue: 'Keyword already added' })
      );
      return;
    }

    setKeywordError(null);
    const updatedKeywords = [...keywords, trimmed];
    onConfigChange?.({
      ...(config || { enabled: true }),
      keywords: updatedKeywords,
    });
    setKeywordInput('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleRemoveKeyword = (indexToRemove: number) => {
    const updatedKeywords = keywords.filter((_, idx) => idx !== indexToRemove);
    onConfigChange?.({
      ...(config || { enabled: true }),
      keywords: updatedKeywords,
    });
  };

  return (
    <div
      className={`flex flex-col gap-4 rounded-lg border bg-card p-4 transition-colors ${disabled ? 'opacity-60' : ''
        }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-white/10 dark:text-foreground">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-foreground">
                {name || meta.title}
              </span>
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                {guardrailId}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {description || meta.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-center">
          <Switch
            checked={enabled}
            onCheckedChange={onToggleEnabled}
            disabled={disabled}
            aria-label={`Toggle ${name || meta.title}`}
          />
          {/* <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={disabled}
            className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title={t('REMOVE', { defaultValue: 'Remove' })}
          >
            <Trash2 className="h-4 w-4" />
          </Button> */}
        </div>
      </div>

      {keywordsParam && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-foreground">
              {t('RESTRICTED_KEYWORDS', { defaultValue: 'Restricted Keywords & Terms' })}
            </Label>
            <span className="text-[11px] text-muted-foreground">
              {t('MAX_CHARS_PER_TERM', {
                defaultValue: `Max ${maxItemLength} chars per term`,
                max: maxItemLength,
              })}
            </span>
          </div>

          <div className="flex gap-2">
            <Input
              value={keywordInput}
              onChange={(e) => {
                setKeywordInput(e.target.value);
                if (keywordError) setKeywordError(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('TYPE_A_TERM_AND_PRESS_ENTER', {
                defaultValue: 'Type keyword (e.g. C++, confidential) and press Enter',
              })}
              disabled={disabled || !enabled}
              maxLength={maxItemLength}
              className="h-9 text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddKeyword}
              disabled={disabled || !enabled || !keywordInput.trim()}
              className="h-9 px-3"
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t('ADD', { defaultValue: 'Add' })}
            </Button>
          </div>

          {keywordError && (
            <p className="text-xs text-destructive">{keywordError}</p>
          )}

          {errorMessage && (
            <p className="text-xs text-destructive">{errorMessage}</p>
          )}

          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {keywords.map((kw, idx) => (
                <Badge
                  key={`${kw}-${idx}`}
                  variant="secondary"
                  className="flex items-center gap-1 py-0.5 pl-2 pr-1 text-xs font-normal"
                >
                  <span className="font-mono">{kw}</span>
                  {!disabled && enabled && (
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(idx)}
                      className="rounded-full p-0.5 hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
